// src/components/admin/psat/reports/PSATStudentReportViewer.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../../../../firebaseConfig";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useReactToPrint } from "react-to-print";
import { RiErrorWarningFill } from "react-icons/ri";
import { FaCheckCircle } from "react-icons/fa";

// Reuse SAT report sub-components, passing PSAT collection name
import DetailedQuestionReport from "../../dsat/reports/DetailedQuestionReport";
import PerformanceReport from "../../dsat/reports/PerformanceReport";
import SubcategoryBreakdown from "../../dsat/reports/SubcategoryBreakdown";
import SearchableStudentDropdown from "../../dsat/reports/SearchableStudentDropdown";

const PSAT_COLLECTION = "psat_diagnostic_tests";
const PSAT_RESULT_TYPE = "PSAT";

// PSAT score ranges differ from SAT (320–1520 total, 160–760 per section)
const PSAT_SCORE_CONFIG = {
  totalMax: 1520,
  totalRange: "320–1520",
  sectionMax: 760,
  sectionRange: "160–760",
};
const PSAT_RW_TOTAL = 54;
const PSAT_MATH_TOTAL = 44;

// ─── helpers ────────────────────────────────────────────────────────────────

function toDateSafe(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateShort(v) {
  const d = toDateSafe(v);
  if (!d) return "—";
  return d.toLocaleDateString();
}

/**
 * Build the attempt dropdown label.
 * Shows scaled total score if available, matching SAT's pattern.
 */
function buildAttemptLabel(attempt) {
  const title =
    attempt?.testTitle ||
    attempt?.testName ||
    attempt?.displayName ||
    (attempt?.testId ? `PSAT Diagnostic (${attempt.testId})` : "PSAT Diagnostic");

  const completed = attempt?.completedAt || attempt?.completed_on;
  const dateLabel = completed ? formatDateShort(completed) : "—";

  // Prefer scaled total; fall back to raw if no table was uploaded
  const score = attempt?.psatTotalScaled ?? attempt?.totalRawScore ?? null;
  const scoreLabel = score == null ? "" : ` — ${score}`;

  return `${title} — Completed ${dateLabel}${scoreLabel}`;
}

/**
 * Derive which Module 2 was taken from saved field or pathHistory fallback.
 * subject: 'english' | 'math'
 */
function getModule2(attempt, subject) {
  if (subject === "english") {
    if (attempt?.englishModule2) return attempt.englishModule2;
    if (Array.isArray(attempt?.pathHistory)) {
      return (
        attempt.pathHistory.find(
          (h) => h.module === "English_Module_A" || h.module === "English_Module_B"
        )?.module || null
      );
    }
    return null;
  } else {
    if (attempt?.mathModule2) return attempt.mathModule2;
    if (Array.isArray(attempt?.pathHistory)) {
      return (
        attempt.pathHistory.find(
          (h) => h.module === "Math_Module_A" || h.module === "Math_Module_B"
        )?.module || null
      );
    }
    return null;
  }
}

function lookupScale(table, tableKey, rawScore) {
  if (!table || rawScore == null) return null;
  const map = table[tableKey];
  if (!map) return null;
  const v = map[String(rawScore)];
  return v != null ? v : null;
}

function resolveTotal(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function formatRawScoreDisplay(raw, total) {
  if (raw === "—" || raw == null || raw === "") return "—";
  return `${raw} / ${total}`;
}

/**
 * Augment a PSAT attempt with computed scaled scores.
 * Adds fields that PerformanceReport and tiles expect:
 *   psatTotalScaled, psatEnglishScaled, psatMathScaled, _hasScoreTable
 *   totalScore, totalScaled, rwScaledScore, mathScaledScore  (PerformanceReport field names)
 */
function augmentWithScores(attempt, table) {
  if (!table) return attempt;

  const eng2 = attempt.englishModule2 || getModule2(attempt, "english");
  const math2 = attempt.mathModule2 || getModule2(attempt, "math");

  const engRaw  = attempt.englishRawScore ?? attempt.rwRawScore ?? null;
  const mathRaw = attempt.mathRawScore ?? attempt.mathRaw ?? null;

  const engScaled  = lookupScale(table, eng2  === "English_Module_B" ? "rwModuleB"   : "rwModuleA",   engRaw);
  const mathScaled = lookupScale(table, math2 === "Math_Module_B"    ? "mathModuleB" : "mathModuleA", mathRaw);
  const totalScaled = engScaled != null && mathScaled != null ? engScaled + mathScaled : null;

  return {
    ...attempt,
    _hasScoreTable: true,
    psatTotalScaled:   totalScaled,
    psatEnglishScaled: engScaled,
    psatMathScaled:    mathScaled,
    // Field names PerformanceReport reads from `summary`
    totalScore:     totalScaled,
    totalScaled:    totalScaled,
    rwScaledScore:  engScaled,
    mathScaledScore: mathScaled,
  };
}

async function fetchPSATAttemptsForStudent(studentId) {
  const results = [];
  try {
    const q = query(
      collection(db, "diagnostic_results"),
      where("studentId", "==", studentId)
    );
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = d.data();
      if (data.type !== PSAT_RESULT_TYPE) return;
      if (data.isArchived) return;
      if (data.status === "completed" || data.completedAt) {
        results.push({ attemptId: d.id, ...data });
      }
    });
  } catch (e) {
    console.warn("Error fetching PSAT diagnostic_results:", e);
  }
  return results;
}

// ─── styles — matches SAT StudentReportViewer exactly ───────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px 16px",
    // Exactly matches SAT report background gradient
    background: "radial-gradient(circle at top, #e0f2fe 0, #eef2ff 40%, #f9fafb 100%)",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  container: { maxWidth: 1100, margin: "0 auto" },
  topCard: {
    borderRadius: 18,
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(229,231,235,0.9)",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
    backdropFilter: "blur(8px)",
    overflow: "visible",
  },
  header: {
    padding: "18px 18px 10px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  titleBlock: { display: "flex", flexDirection: "column", gap: 4 },
  title: { margin: 0, fontSize: 20, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" },
  subtitle: { margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.35 },
  // Same badge style as SAT (blue, not purple)
  badge: {
    fontSize: 11, padding: "6px 10px", borderRadius: 999,
    background: "linear-gradient(90deg, #dbeafe, #e0e7ff)",
    color: "#1d4ed8", border: "1px solid #bfdbfe",
    fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
  },
  controls: {
    padding: "12px 18px 16px",
    borderTop: "1px solid rgba(226,232,240,0.8)",
    display: "grid",
    gridTemplateColumns: "1.2fr 1.8fr",
    gap: 12,
    alignItems: "start",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12, color: "#334155", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" },
  select: {
    width: "100%", fontSize: 14, padding: "10px 12px",
    borderRadius: 12, border: "1px solid #d1d5db",
    backgroundColor: "#ffffff", outline: "none",
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    boxSizing: "border-box", height: 46, minHeight: 44,
  },
  hint: { fontSize: 12, color: "#64748b", marginTop: 2 },
  reportShell: {
    marginTop: 16, borderRadius: 18, backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)", overflow: "hidden",
  },
  reportHeader: {
    padding: "16px 18px", borderBottom: "1px solid #e5e7eb",
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    gap: 12, background: "linear-gradient(90deg, #ffffff, #f8fafc)",
  },
  studentName: { margin: 0, fontSize: 18, fontWeight: 900, color: "#0f172a" },
  studentEmail: { margin: "2px 0 0", fontSize: 13, color: "#64748b" },
  metaBlock: { textAlign: "right", color: "#64748b", fontSize: 12 },
  metaLabel: { fontSize: 11, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b", marginBottom: 2 },
  metaValue: { fontSize: 13, fontWeight: 700, color: "#0f172a" },
  // Exactly 5 tiles, matching SAT layout and gradient
  tiles: {
    padding: "14px 18px 6px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 12,
  },
  tile: {
    borderRadius: 14, padding: "12px 12px",
    border: "1px solid #e5e7eb",
    background: "linear-gradient(145deg, #f8fafc, #eef2ff)",
  },
  tileValue: { fontSize: 26, fontWeight: 900, color: "#1d4ed8", marginBottom: 4 },
  tileLabel: { fontSize: 12, color: "#64748b" },
  scoringNote: {
    margin: "0 18px 8px",
    padding: "8px 12px",
    borderRadius: 8,
    background: "#fffbeb",
    border: "1px solid #fcd34d",
    fontSize: 12,
    color: "#92400e",
  },
  actions: {
    padding: "12px 18px 16px",
    display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
  },
  btn: { fontSize: 13, padding: "9px 14px", borderRadius: 999, border: "1px solid transparent", cursor: "pointer", fontWeight: 700 },
  btnDark: { backgroundColor: "#0f172a", color: "#ffffff", boxShadow: "0 10px 20px rgba(15,23,42,0.16)" },
  btnBlue: { backgroundColor: "#1d4ed8", color: "#ffffff", boxShadow: "0 10px 20px rgba(37,99,235,0.25)" },
  btnGhost: { backgroundColor: "#ffffff", color: "#0f172a", borderColor: "#e5e7eb" },
  loading: { padding: 16, borderRadius: 14, background: "#f1f5f9", color: "#475569", textAlign: "center", border: "1px solid #e2e8f0" },
  error: { padding: 14, borderRadius: 14, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: 13 },
  empty: { padding: "10px 18px 16px", color: "#64748b", fontSize: 13 },
};

const printPageStyle = `
@page { size: auto; margin: 16mm; }
body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #ffffff !important; }
.no-print { display: none !important; }
table, tr, td, th { break-inside: avoid; page-break-inside: avoid; }
`;

// ─── component ───────────────────────────────────────────────────────────────

export default function PSATStudentReportViewer({ preselectedStudentId = "" }) {
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState("");

  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [attempts, setAttempts] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [attemptsError, setAttemptsError] = useState("");
  const [selectedAttemptId, setSelectedAttemptId] = useState("");

  const [showDetails, setShowDetails] = useState(false);
  const [showIntegrityTooltip, setShowIntegrityTooltip] = useState(false);

  const reportRef = useRef(null);
  const [searchParams] = useSearchParams();
  const urlStudentId = String(searchParams.get("studentId") || "").trim();
  const urlAttemptId = String(searchParams.get("attemptId") || "").trim();
  const normalizedPreselectedId = String(preselectedStudentId || "").trim() || urlStudentId;

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Student PSAT Diagnostic Report",
    pageStyle: printPageStyle,
  });

  // ── load students who have PSAT results ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function loadStudents() {
      setStudentsLoading(true);
      setStudentsError("");
      try {
        const snapshot = await getDocs(collection(db, "diagnostic_results"));
        const studentMap = new Map();
        snapshot.forEach((docSnap) => {
          const d = docSnap.data() || {};
          if (d.type !== PSAT_RESULT_TYPE) return;
          if (d.isArchived) return;
          const sid = d.studentId;
          if (!sid) return;
          if (!(d.status === "completed" || !!d.completedAt)) return;
          const timeVal = (toDateSafe(d.completedAt)?.getTime()) || 0;
          if (!studentMap.has(sid) || timeVal > studentMap.get(sid)) {
            studentMap.set(sid, timeVal);
          }
        });

        const uniqueIds = Array.from(studentMap.keys());
        if (uniqueIds.length === 0) {
          if (!cancelled) setStudents([]);
          return;
        }

        const loaded = (
          await Promise.all(
            uniqueIds.map(async (uid) => {
              try {
                const userSnap = await getDoc(doc(db, "users", uid));
                const userData = userSnap.exists() ? userSnap.data() : {};
                return {
                  uid,
                  name: userData.name || "Unknown",
                  email: userData.email || "",
                  lastCompletedAt: studentMap.get(uid) || 0,
                  lastDateLabel: studentMap.get(uid) > 0
                    ? new Date(studentMap.get(uid)).toLocaleDateString()
                    : "N/A",
                };
              } catch {
                return { uid, name: "Unknown (Error)", email: "", lastCompletedAt: studentMap.get(uid) || 0, lastDateLabel: "N/A" };
              }
            })
          )
        ).sort((a, b) => b.lastCompletedAt - a.lastCompletedAt);

        if (!cancelled) {
          setStudents(loaded);
          if (normalizedPreselectedId) setSelectedStudentId(normalizedPreselectedId);
        }
      } catch (e) {
        if (!cancelled) setStudentsError(`Failed to load students: ${e?.message || e}`);
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    }

    loadStudents();
    return () => { cancelled = true; };
  }, [normalizedPreselectedId]);

  // ── load attempts + scoring tables when student changes ──────────────────
  useEffect(() => {
    let cancelled = false;

    async function loadAttempts() {
      setAttempts([]);
      setSelectedAttemptId("");
      setAttemptsError("");
      setShowDetails(false);
      if (!selectedStudentId) return;

      setAttemptsLoading(true);
      try {
        const list = await fetchPSATAttemptsForStudent(selectedStudentId);
        if (cancelled) return;

        const sorted = [...list].sort((a, b) => {
          const da = toDateSafe(a?.completedAt) || new Date(0);
          const db_ = toDateSafe(b?.completedAt) || new Date(0);
          return db_.getTime() - da.getTime();
        });

        // Fetch scoring tables for every unique testId in parallel
        const uniqueTestIds = [...new Set(sorted.map((a) => a.testId).filter(Boolean))];
        const tables = {};
        await Promise.all(
          uniqueTestIds.map(async (tid) => {
            try {
              const snap = await getDoc(
                doc(db, "psat_diagnostic_tests", tid, "scoring", "psat_score_table")
              );
              if (snap.exists()) tables[tid] = snap.data();
            } catch (e) {
              console.warn(`Could not load scoring table for ${tid}:`, e);
            }
          })
        );

        // Augment every attempt with computed scaled scores
        const augmented = sorted.map((attempt) =>
          augmentWithScores(attempt, tables[attempt.testId] || null)
        );

        if (cancelled) return;
        setAttempts(augmented);
        const preferredAttemptId =
          urlAttemptId && augmented.some((a) => a.attemptId === urlAttemptId)
            ? urlAttemptId
            : augmented[0]?.attemptId || "";
        setSelectedAttemptId(preferredAttemptId);
      } catch (e) {
        if (!cancelled) setAttemptsError(`Failed to load attempts: ${e?.message || e}`);
      } finally {
        if (!cancelled) setAttemptsLoading(false);
      }
    }

    loadAttempts();
    return () => { cancelled = true; };
  }, [selectedStudentId, urlAttemptId]);

  // ── derived values ────────────────────────────────────────────────────────
  const selectedStudent = useMemo(
    () => students.find((s) => s.uid === selectedStudentId) || null,
    [students, selectedStudentId]
  );
  const selectedAttempt = useMemo(
    () => attempts.find((a) => a.attemptId === selectedAttemptId) || null,
    [attempts, selectedAttemptId]
  );

  const isCompleted = selectedAttempt && (selectedAttempt.status === "completed" || !!selectedAttempt.completedAt);
  const attemptTitle = selectedAttempt?.testTitle || selectedAttempt?.testName || "PSAT Diagnostic";
  const completedLabel = selectedAttempt?.completedAt ? formatDateShort(selectedAttempt.completedAt) : "—";

  // 5 tile values — scaled when available, raw fallback
  const totalScore  = selectedAttempt?.psatTotalScaled   ?? selectedAttempt?.totalRawScore   ?? "—";
  const rwScaled    = selectedAttempt?.psatEnglishScaled ?? "—";
  const mathScaled  = selectedAttempt?.psatMathScaled    ?? "—";
  const rwRaw       = selectedAttempt?.englishRawScore   ?? selectedAttempt?.rwRawScore       ?? "—";
  const mathRaw     = selectedAttempt?.mathRawScore      ?? "—";
  const rwRawTotal = resolveTotal(
    selectedAttempt?.rwTotalQuestions ?? selectedAttempt?.englishTotalQuestions ?? selectedAttempt?.rwQuestionCount,
    PSAT_RW_TOTAL
  );
  const mathRawTotal = resolveTotal(
    selectedAttempt?.mathTotalQuestions ?? selectedAttempt?.mathQuestionCount,
    PSAT_MATH_TOTAL
  );
  const rwRawDisplay = formatRawScoreDisplay(rwRaw, rwRawTotal);
  const mathRawDisplay = formatRawScoreDisplay(mathRaw, mathRawTotal);

  const hasScoreTable   = selectedAttempt?._hasScoreTable === true;
  const scoresResolved  = selectedAttempt?.psatTotalScaled != null;

  // ── render ────────────────────────────────────────────────────────────────
  if (studentsLoading) {
    return <div style={styles.page}><div style={styles.container}><div style={styles.loading}>Loading PSAT reports…</div></div></div>;
  }
  if (studentsError) {
    return <div style={styles.page}><div style={styles.container}><div style={styles.error}>{studentsError}</div></div></div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* ── top card: title + selectors ── */}
        <div style={styles.topCard}>
          <div className="no-print" style={styles.header}>
            <div style={styles.titleBlock}>
              <h2 style={styles.title}>PSAT Diagnostic Report</h2>
              <p style={styles.subtitle}>
                Select a student and a PSAT attempt to view scores, skill breakdown, and question review.
              </p>
            </div>
            <div style={styles.badge}>Reports Dashboard</div>
          </div>

          <div className="no-print" style={styles.controls}>
            <div style={styles.field}>
              <div style={styles.label}>Student</div>
              <SearchableStudentDropdown
                students={students}
                selectedId={selectedStudentId}
                onSelect={(uid) => setSelectedStudentId(uid)}
                placeholder="Search for a student..."
              />
              <div style={styles.hint}>
                {selectedStudentId ? "Student selected." : "Pick a student to load attempts."}
              </div>
            </div>

            <div style={styles.field}>
              <div style={styles.label}>Attempt / Test</div>
              <select
                style={{ ...styles.select, opacity: selectedStudentId && !attemptsLoading ? 1 : 0.75 }}
                value={selectedAttemptId}
                onChange={(e) => { setSelectedAttemptId(e.target.value); setShowDetails(false); }}
                disabled={!selectedStudentId || attemptsLoading || attempts.length === 0}
              >
                {!selectedStudentId && <option value="">Select a student first</option>}
                {selectedStudentId && attemptsLoading && <option value="">Loading attempts…</option>}
                {selectedStudentId && !attemptsLoading && attempts.length === 0 && (
                  <option value="">No completed PSAT attempts found</option>
                )}
                {attempts.map((a) => (
                  <option key={a.attemptId} value={a.attemptId}>
                    {buildAttemptLabel(a)}
                  </option>
                ))}
              </select>
              <div style={styles.hint}>
                {attemptsError || (selectedStudentId
                  ? attemptsLoading ? "Loading attempts…"
                    : attempts.length ? `${attempts.length} attempt(s) found.`
                    : "No PSAT attempts found for this student."
                  : " ")}
              </div>
            </div>
          </div>
        </div>

        {selectedStudentId && !attemptsLoading && attempts.length === 0 && (
          <div style={styles.empty}>No PSAT diagnostic attempts found for this student.</div>
        )}

        {/* ── report card ── */}
        {selectedStudent && selectedAttempt && (
          <div style={styles.reportShell} ref={reportRef}>
            {/* header row */}
            <div style={styles.reportHeader}>
              <div>
                <h3 style={styles.studentName}>{selectedStudent.name}</h3>
                <p style={styles.studentEmail}>{selectedStudent.email}</p>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                  <strong style={{ color: "#0f172a" }}>{attemptTitle}</strong>
                  {" • "}Attempt ID: {selectedAttemptId}
                </div>
              </div>
              <div style={styles.metaBlock}>
                <div style={styles.metaLabel}>Completed on</div>
                <div style={styles.metaValue}>{completedLabel}</div>
                {selectedAttempt?.integrityLog && (
                  <div
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, marginTop: 8, position: "relative", cursor: "help" }}
                    onMouseEnter={() => setShowIntegrityTooltip(true)}
                    onMouseLeave={() => setShowIntegrityTooltip(false)}
                  >
                    <span style={{ fontSize: 12, fontWeight: 700, color: selectedAttempt.integrityLog.cheated_triggered ? "#b91c1c" : "#15803d" }}>
                      {selectedAttempt.integrityLog.cheated_triggered ? "Integrity Flagged" : "Integrity Verified"}
                    </span>
                    {selectedAttempt.integrityLog.cheated_triggered
                      ? <RiErrorWarningFill size={18} color="#dc2626" />
                      : <FaCheckCircle size={16} color="#16a34a" />}
                    {showIntegrityTooltip && (
                      <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, width: 200, backgroundColor: "#1e293b", color: "#f8fafc", padding: "12px", borderRadius: 8, fontSize: 12, textAlign: "left", zIndex: 50, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}>
                        <div style={{ fontWeight: 800, marginBottom: 8, borderBottom: "1px solid #334155", paddingBottom: 4 }}>Integrity Log</div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>Tab Switches:</span><strong>{selectedAttempt.integrityLog.tabSwitchCount}</strong></div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>Fullscreen Exits:</span><strong>{selectedAttempt.integrityLog.fullscreenExitCount}</strong></div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}><span>Inactive Time:</span><strong>{selectedAttempt.integrityLog.totalInactiveTime} sec</strong></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {!isCompleted ? (
              <div style={styles.empty}>This attempt is not marked as completed.</div>
            ) : (
              <>
                {/* ── 5 score tiles — mirrors SAT exactly ── */}
                <div style={styles.tiles}>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{totalScore}</div>
                    <div style={styles.tileLabel}>Total Score</div>
                  </div>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{rwScaled}</div>
                    <div style={styles.tileLabel}>Reading &amp; Writing Scaled Score</div>
                  </div>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{mathScaled}</div>
                    <div style={styles.tileLabel}>Math Scaled Score</div>
                  </div>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{rwRawDisplay}</div>
                    <div style={styles.tileLabel}>R/W Raw Score</div>
                  </div>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{mathRawDisplay}</div>
                    <div style={styles.tileLabel}>Math Raw Score</div>
                  </div>
                </div>

                {/* Scoring note — only shown when no table is uploaded */}
                {!hasScoreTable && (
                  <div style={styles.scoringNote}>
                    ℹ️ No scoring table found for this test. Go to <strong>PSAT Content → Upload Scoring Table</strong> to enable scaled score display (320–1520 range).
                  </div>
                )}
                {hasScoreTable && !scoresResolved && (
                  <div style={styles.scoringNote}>
                    ⚠️ Scaled scores could not be resolved. Check that the scoring table covers this student's raw score and module path.
                  </div>
                )}

                {/* PerformanceReport — augmented summary already has totalScore/rwScaledScore/mathScaledScore.
                    scoreConfig passes PSAT-specific ranges (320–1520, 160–760) to the donut charts. */}
                <div style={{ padding: "0 18px 4px" }}>
                  <PerformanceReport
                    studentId={selectedStudent.uid}
                    testId={selectedAttempt?.testId}
                    summary={selectedAttempt}
                    diagnosticCollection={PSAT_COLLECTION}
                    scoreConfig={PSAT_SCORE_CONFIG}
                  />
                </div>

                <div style={{ padding: "0 18px 6px" }}>
                  <SubcategoryBreakdown
                    studentId={selectedStudent.uid}
                    testId={selectedAttempt?.testId}
                    attemptId={selectedAttemptId}
                    attemptLabel={completedLabel}
                    answers={selectedAttempt?.answers}
                    diagnosticCollection={PSAT_COLLECTION}
                  />
                </div>

                <div className="no-print" style={styles.actions}>
                  <button style={{ ...styles.btn, ...styles.btnDark }} onClick={() => setShowDetails((v) => !v)}>
                    {showDetails ? "Hide Question Breakdown" : "View Question Breakdown"}
                  </button>
                  <button style={{ ...styles.btn, ...styles.btnBlue }} onClick={() => handlePrint()}>
                    🖨️ Print / Save as PDF
                  </button>
                </div>

                {showDetails && (
                  <>
                    <div style={{ padding: "0 18px 18px" }}>
                      <DetailedQuestionReport
                        studentId={selectedStudent.uid}
                        testId={selectedAttempt?.testId}
                        attemptId={selectedAttemptId}
                        showSubcategory={false}
                        diagnosticCollection={PSAT_COLLECTION}
                      />
                    </div>
                    <div className="no-print" style={{ padding: "0 18px 18px", display: "flex", justifyContent: "flex-end" }}>
                      <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={() => { setShowDetails(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                        Close &amp; Scroll Top
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {!selectedStudentId && (
          <div className="no-print" style={styles.empty}>
            Select a student above to view their PSAT report.
          </div>
        )}
      </div>
    </div>
  );
}
